import { useEffect, useState } from "react";
import {
  approveIdentityReviewIssue,
  correctIdentityReviewIssue,
  dismissIdentityReviewIssue,
  fetchIdentityReviewDetail,
} from "../../../../services/vehicleNormalizationApi";

function formatValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

const VehicleIdentityReviewDetailPanel = ({ issueId, canManage, onClose, onUpdated }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!issueId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const data = await fetchIdentityReviewDetail(issueId);
        if (!cancelled) setDetail(data);
      } catch (error) {
        if (!cancelled) setErrorMessage(error.message || "Unable to load issue detail.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [issueId]);

  const diagnostics = detail?.diagnostics;
  const issue = detail?.issue;

  const runAction = async (action) => {
    if (!issueId || !canManage) return;
    try {
      if (action === "approve") await approveIdentityReviewIssue(issueId, { note });
      if (action === "dismiss") await dismissIdentityReviewIssue(issueId, { note });
      if (action === "correct") {
        await correctIdentityReviewIssue(issueId, {
          note,
          makeNormalized: diagnostics?.selected?.make,
          modelNormalized: diagnostics?.selected?.model,
          trimNormalized: diagnostics?.selected?.trim,
        });
      }
      onUpdated?.();
      onClose?.();
    } catch (error) {
      setErrorMessage(error.message || "Action failed.");
    }
  };

  if (!issueId) return null;

  return (
    <section className="panel-card admin-vehicle-identity-review-detail">
      <header className="admin-vehicle-identity-review-detail__header">
        <div>
          <h2>Identity review detail</h2>
          <p className="muted">{issue?.diagnosticMetadata?.label || issue?.issueType}</p>
        </div>
        <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
          Close
        </button>
      </header>

      {loading ? <p className="muted">Loading diagnostics…</p> : null}
      {errorMessage ? <p className="admin-state-panel admin-state-panel--error">{errorMessage}</p> : null}

      {!loading && diagnostics ? (
        <>
          <div className="admin-vehicle-identity-review-detail__grid">
            <div>
              <h3>Listing context</h3>
              <p>Listing: {formatValue(diagnostics.listingId)}</p>
              <p>Source: {formatValue(diagnostics.sourceName || diagnostics.sourceKey)}</p>
              <p>Canonical title: {formatValue(diagnostics.canonicalTitle)}</p>
              <p>Confidence: {formatValue(diagnostics.confidence)}</p>
            </div>
            <div>
              <h3>Raw source identity</h3>
              <p>Make: {formatValue(diagnostics.raw.make)}</p>
              <p>Model: {formatValue(diagnostics.raw.model)}</p>
              <p>Trim: {formatValue(diagnostics.raw.trim)}</p>
            </div>
            <div>
              <h3>Selected identity</h3>
              <p>Make: {formatValue(diagnostics.selected.make)}</p>
              <p>Model: {formatValue(diagnostics.selected.model)}</p>
              <p>Trim: {formatValue(diagnostics.selected.trim)}</p>
            </div>
          </div>

          <div className="admin-vehicle-identity-review-detail__comparison">
            <h3>Before and after</h3>
            <p>
              <strong>Raw model:</strong> {formatValue(diagnostics.comparison.rawModel)}
            </p>
            <p>
              <strong>Sanitized model:</strong> {formatValue(diagnostics.comparison.sanitizedModel)}
            </p>
            <p>
              <strong>Lookup model:</strong> {formatValue(diagnostics.comparison.lookupModel)}
            </p>
            <p>
              <strong>Selected model:</strong> {formatValue(diagnostics.comparison.selectedModel)}
            </p>
            {diagnostics.comparison.detectedPollution?.length ? (
              <ul>
                {diagnostics.comparison.detectedPollution.map((token) => (
                  <li key={token.type}>
                    {token.label}
                    {token.token ? ` — ${token.token}` : ""}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">No pollution tokens detected.</p>
            )}
          </div>

          {canManage ? (
            <div className="admin-vehicle-identity-review-detail__actions">
              <textarea
                placeholder="Resolution note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
              />
              <div className="admin-dashboard__header-actions">
                <button type="button" className="admin-dashboard__action-btn" onClick={() => runAction("approve")}>
                  Approve selected identity
                </button>
                <button type="button" className="admin-dashboard__action-btn" onClick={() => runAction("correct")}>
                  Keep selected identity (manual)
                </button>
                <button type="button" className="admin-dashboard__action-btn" onClick={() => runAction("dismiss")}>
                  Dismiss issue
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default VehicleIdentityReviewDetailPanel;
